import { error, readableTimestamp } from "@zyzgroup/core-common";
import { documentVisible$, onDocumentReady } from "./dom";

// 缓存策略要正确，否则问题会被放大
// 最佳缓存策略：入口文件要尽快更新，静态资源要放心缓存，版本文件要实时可读

// index.html 不要强缓存
// index.html是整个 SPA 的入口
// 如果它被长时间缓存，用户就可能始终拿不到新的资源入口映射
// 推荐策略：no-cache 或 更严格的 no-store
// 例如 Nginx：
// location =/index.html {
//   add_header Cache-Control "no-cache, no-store, must-revalidate";
// }

// 带 hash 的 js/css 可以强缓存
// 这类资源天然适合长期缓存，因为文件名已经包含内容签名
// 只要内容变化，hash 就会变，浏览器就会自动拉新文件, 这样可以显著提升加载性能
// 例如：
// location /assets/ {
//   add_header Cache-Control"public, max-age=31536000, immutable";
// }

export class WebMonitor {
  appId: string;
  reportUrl?: string;

  constructor(options: {
    appId: string;
    reportUrl?: string;
    checkWhiteScreen?: boolean;
  }) {
    this.appId = options.appId;
    this.reportUrl = options.reportUrl;
    this.listenJSRuntimeError();
    this.listenAssetsLoadError();
    this.listenPromiseError();
    if (options.checkWhiteScreen) {
      this.listenWhiteScreenError();
    }
  }

  // 捕获绝大多数运行时错误
  public listenJSRuntimeError() {
    window.onerror = (message, source, lineno, colno, error) => {
      const log = {
        type: "js_runtime_error",
        msg: message,
        file: source,
        row: lineno,
        col: colno,
        stack: error?.stack
      };
      this.report(log);
    };
  }

  // 资源加载错误
  public listenAssetsLoadError() {
    window.addEventListener(
      "error",
      (event) => {
        const target = event.target;
        // 排除 JS 报错
        if (
          target instanceof HTMLScriptElement ||
          target instanceof HTMLStyleElement ||
          target instanceof HTMLImageElement
        ) {
          const log = {
            type: "resource_load_error",
            filename: (target as any).src || (target as any).href,
            tagName: target.tagName
          };
          this.report(log);
        }
      },
      true // 资源错误不会冒泡
    );
  }

  // Promise 报错
  public listenPromiseError() {
    window.addEventListener(
      "unhandledrejection",
      (event: PromiseRejectionEvent) => {
        const log = {
          type: "promise_error",
          reason: event.reason
        };
        this.report(log);
      }
    );
  }

  // 白屏检测
  public listenWhiteScreenError() {
    // 页面加载 3 秒后检测
    onDocumentReady(() => {
      setTimeout(() => {
        const points = [
          [window.innerWidth / 2, window.innerHeight / 2], // 中心点
          [window.innerWidth / 2, window.innerHeight * 0.2], // 上
          [window.innerWidth / 2, window.innerHeight * 0.8], // 下
          [window.innerWidth * 0.2, window.innerHeight / 2], // 左
          [window.innerWidth * 0.8, window.innerHeight / 2] // 右
          // ... 可以多取几个点
        ];

        let wrapperElements = 0;

        points.forEach((point) => {
          const [x, y] = point;
          // 获取坐标处的元素列表
          const elements = document.elementsFromPoint(x, y);
          const topElement = elements[0]; // 最上层元素

          // 如果最上层是 body 或 html 或 骨架屏的那个div，说明没内容覆盖
          // UNDO : 骨架屏检查
          if (
            topElement?.tagName === "BODY" ||
            topElement?.tagName === "HTML"
          ) {
            wrapperElements++;
          }
        });

        // 如果 80% 的采样点都是空壳，判定白屏
        if (wrapperElements >= points.length * 0.8) {
          const log = {
            type: "whitescreen_error",
            reason: "检测到白屏！"
          };
          this.report(log);
        }
      }, 3000);
    });
  }

  // 数据上报：别用 AJAX
  // 如果页面正在关闭或卸载（用户看到白屏气得关网页），AJAX 请求很可能会被浏览器切断（Canceled），导致上报失败
  // 标准方案：navigator.sendBeacon
  // 不占用主线程：它把数据放入浏览器的传输队列，浏览器会在空闲时发送
  // 页面关闭也能发：即使用户强制关闭 Tab，浏览器也会保证把这条请求发出去
  public report(data: Record<string, any>) {
    const payload = JSON.stringify({
      ...data,
      appId: this.appId,
      time: readableTimestamp()
    });
    // error(payload);
    console.error(payload);
    if (this.reportUrl) {
      navigator.sendBeacon(this.reportUrl, payload);
    }
  }

  // 计算一些关键的性能指标
  // public performance() {
  //   window.addEventListener("load", (event) => {
  //     // Time to Interactive 可交互时间
  //     let timing: PerformanceEntry =
  //       performance.getEntriesByType("navigation")[0];
  //     let tti = timing.domInteractive - timing.fetchStart;
  //   });
  // }

  // 网络连接状态
  // public networkStatus() {
  //   const connection =
  //     navigator.connection ||
  //     navigator.mozConnection ||
  //     navigator.webkitConnection;
  //   const type = connection.effectiveType;
  //   function updateConnectionStatus() {
  //     console.log(
  //       "Connection type changed from " +
  //         type +
  //         " to " +
  //         connection.effectiveType
  //     );
  //     type = connection.effectiveType;
  //   }
  //   connection.addEventListener("change", updateConnectionStatus);
  // }
}

// 获取当前版本
function getCurrentVersion() {
  return document
    .querySelector('meta[name="app-version"]')
    ?.getAttribute("content");
}

// 获取最新版本
async function fetchLatestVersionFromHtml() {
  const res = await fetch(`/index.html?t=${Date.now()}`, {
    cache: "no-store"
  });
  const html = await res.text();
  const match = html.match(
    /<meta\s+name=["']app-version["']\s+content=["']([^"']+)["']/
  );
  return match ? match[1] : null;
}

// 版本检测 + 刷新提示
export async function checkVersion() {
  try {
    const currentVersion = getCurrentVersion();
    const latestVersion = await fetchLatestVersionFromHtml();
    if (currentVersion && latestVersion && currentVersion !== latestVersion) {
      // 发现新版本后，提示用户刷新
      const ok = window.confirm("系统已更新，是否立即刷新页面？");
      if (ok) {
        window.location.reload();
      }
    }
  } catch (err) {
    console.error("版本检测失败:", err);
  }
}

// checkVersion();
// setInterval(checkVersion, 5 * 60 * 1000);
// documentVisible$().subscribe((visible) => {
//   if (visible) {
//     checkVersion();
//   }
// });

// 场景：
// - 版本检测还没来得及执行
// - 用户刚好在检测间隔内点击了菜单
// - 服务端刚完成发布
// - 某个懒加载 chunk 已经失效
// 这时候: 问题已经发生了, 如果没有兜底机制，用户就会直接看到报错或白屏
function isChunkLoadError(error: any) {
  const message =
    error?.message ?? error?.reason?.message ?? typeof error == "string"
      ? error
      : "";
  return (
    message.includes("Loading chunk") ||
    message.includes("ChunkLoadError") ||
    message.includes("Failed to fetch dynamically imported module")
  );
}

// 为什么只自动刷新一次?
// 因为如果失败原因不是“版本切换”，而是：
// - 网络异常
// - CDN 故障
// - 资源服务器不可用
// - 权限拦截
// 那么无限刷新只会让问题更严重，甚至让用户完全无法操作
// 所以最佳实践是：
// - 自动刷新一次尝试恢复
// - 如果仍失败，再提示用户手动处理或联系支持人员
function handleChunkLoadError() {
  const key = "app_chunk_reload_once";
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, "1");
    alert("系统资源已更新，正在为您刷新页面");
    window.location.reload();
  } else {
    console.error("刷新后仍然失败，请提示用户手动刷新或联系管理员");
  }
}
// 页面正常加载成功后清理标记：
// window.addEventListener("load", () => {
//   sessionStorage.removeItem("app_chunk_reload_once");
// });

// 监听 vite 预加载错误，如果发生错误，则重新加载页面
// window.addEventListener("vite:preloadError", () => {
//   window.location.reload();
// });
// window.addEventListener("error", (event) => {
//   if (isChunkLoadError(event.error || event)) {
//     handleChunkLoadError();
//   }
// });
// window.addEventListener("unhandledrejection", (event) => {
//   if (isChunkLoadError(event.reason || event)) {
//     handleChunkLoadError();
//   }
// });
